import React, { useEffect, useState } from 'react'
import axios from 'axios'
import styled from 'styled-components'
import {
    Box,
    Button,
    ButtonGroup,
    CardMedia,
    Chip,
    Grid,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Typography,
} from '@mui/material'
import { KEYS } from 'utils/intl/constant'
import { translate } from 'utils/intl/helper'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { selectSelected } from '../../../../store/reducer/candidate'
import { addList as reportAddList } from '../../../../store/reducer/report'
import constant from '../../../utils/constant'
import { getFilename } from '../../../utils/functions'
import EnergyLabelPreview from './EnergyLabelPreview'

const StyledCardMedia = styled(CardMedia)`
    && {
        object-fit: contain;
        height: 120px;
    }
`

const WrapInfo = styled.div`
    flex: 0 0 50%;
    display: flex;
    justify-content: center;
    flex-direction: column;
`

const TextInfo = styled(Typography)`
    && {
        color: #757575;
        font-size: 14px;
    }
`

const StyledTextInfo = styled(TextInfo)`
    && {
        margin-bottom: 16px;
    }
`

const StyledTextInfoFlex = styled(TextInfo)`
    && {
        flex: 1 1 100%;
    }
`

const NewLineContainer = styled(Box)`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 4px;
`

const StyledLinearProgress = styled(LinearProgress)`
    && {
        margin-top: 2em;
    }
`

const StyledBoxCenter = styled(Box)`
    display: flex;
    justify-content: center;
`

const TextError = styled(Typography)`
    && {
        color: #ff0000;
        font-size: 14px;
    }
`

const StyledBoxFullWidth = styled(Box)`
    width: 100%;
`

const ModelInfoLayout = styled(Box)`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
`

const ModelInfoContentColumn = styled(Box)`
    display: flex;
    flex-direction: column;
    gap: 4px;
`

const ModelInfoTitle = styled(TextInfo)`
    && {
        margin-bottom: 0;
    }
`

interface DetailsHeaderProps {
    defaultData: any
    currentRangeIndex?: string
    onRangeIndexChange: (value: string) => void
    rangeIndices: string[]
}

const DetailsHeader: React.FC<DetailsHeaderProps> = ({
    defaultData,
    currentRangeIndex,
    onRangeIndexChange,
    rangeIndices,
}) => {
    const dispatch = useAppDispatch()
    const selectedData = useAppSelector((state) => selectSelected(state.candidate))
    const intl = useAppSelector((state) => state.ui.intl)
    const selectedLang: string = useAppSelector((state) => state.ui.lang)

    const [pdfBlob, setPdfBlob] = useState<Blob | null>(null)
    const [loader, setLoader] = useState(false)
    const [error, setError] = useState<any>(null)

    const getPdfBlob = async (type: string) => {
        if (!selectedData) return
        setLoader(true)
        setError(null)
        try {
            const tdsLink =
                constant.backend.GENERATE_TDS +
                selectedData.model +
                '/' +
                selectedData.id +
                '/' +
                type +
                '/' +
                selectedData.size +
                '/' +
                selectedLang

            const response = await axios.get(tdsLink, {
                responseType: 'arraybuffer',
            })
            const blob = new Blob([response.data], { type: 'application/pdf' })
            setPdfBlob(blob)
            setLoader(false)
        } catch (err: any) {
            console.error(err)
            setError(err)
            setLoader(false)
        }
    }

    const handleClickButton = (type: string) => {
        getPdfBlob(type)
    }

    useEffect(() => {
        if (pdfBlob && selectedData) {
            dispatch(
                reportAddList({
                    pdfBlob,
                    model: selectedData.info?.modelname ?? selectedData.model?.toUpperCase(),
                    size: selectedData.size,
                })
            )
        }
    }, [pdfBlob])

    const checkCalctype = (calcType: string) => {
        const ecodesignCalcType = selectedData
            ? Object.keys(selectedData?.ecodesign).filter((name) => new RegExp(calcType).test(name))
            : []

        if (!ecodesignCalcType.length) console.warn(`missing ecodesign ${calcType}`)

        return ecodesignCalcType
    }

    const seer = checkCalctype('^(?!D).*SEER.*')
    const sepr = checkCalctype('^SEPR.*')
    const scop = checkCalctype('^SCOP*').reduce(
        (prev, curr) => ({
            ...prev,
            LT: { ...prev.LT, values: [...prev.LT.values, ...(curr.includes('LT') ? [curr] : [])] },
            MT: { ...prev.MT, values: [...prev.MT.values, ...(curr.includes('MT') ? [curr] : [])] },
            HT: { ...prev.HT, values: [...prev.HT.values, ...(curr.includes('HT') ? [curr] : [])] },
        }),
        {
            LT: { name: KEYS.UI_LOWTEMPERATURE, values: [] as string[] },
            MT: { name: KEYS.UI_MEDIUMTEMPERATURE, values: [] as string[] },
            HT: { name: KEYS.UI_HIGHTEMPERATURE, values: [] as string[] },
        }
    )
    const dseer = checkCalctype('DSEER.*')

    if (!defaultData) return null

    const Item = styled(Paper)(({ theme }) => ({
        ...theme.typography.body2,
        padding: theme.spacing(1),
        margin: '0 8px',
        textAlign: 'left',
        color: theme.palette.text.secondary,
    }))

    return (
        <StyledBoxFullWidth>
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                <Grid item xs={6}>
                    <Grid container>
                        <Grid item xs={6}>
                            <Item variant='outlined' square>
                                <StyledCardMedia
                                    component='img'
                                    image={`${constant.ui.STATIC_ASSETS_STORAGE_URL}${getFilename(
                                        defaultData.info.image.modelImage
                                    )}`}
                                />
                            </Item>
                        </Grid>

                        <WrapInfo>
                            <ModelInfoLayout>
                                <ModelInfoContentColumn>
                                    <ModelInfoTitle>{translate(intl, KEYS.UI_MODEL_INFORMATION)}:</ModelInfoTitle>

                                    <Box display='flex' flexWrap='wrap' gap={1}>
                                        <Chip
                                            label={`${translate(intl, KEYS.UI_MODEL)}: ${
                                                defaultData.info.modelName ?? defaultData.model
                                            }`}
                                            variant='outlined'
                                            size='small'
                                        />
                                        <Chip
                                            label={`${translate(intl, KEYS.UI_SIZE)}: ${defaultData.size}`}
                                            variant='outlined'
                                            size='small'
                                        />
                                    </Box>

                                    <NewLineContainer>
                                        <TextInfo>{translate(intl, KEYS.UI_RANGE_INDEX)}</TextInfo>
                                        <Select
                                            id='select-range-index'
                                            value={currentRangeIndex ?? ''}
                                            onChange={(e) => onRangeIndexChange(e.target.value as string)}
                                            fullWidth
                                            size='small'>
                                            {rangeIndices.map((index) => (
                                                <MenuItem key={index} value={index}>
                                                    {index}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </NewLineContainer>
                                </ModelInfoContentColumn>

                                <EnergyLabelPreview selectedData={selectedData} />
                            </ModelInfoLayout>
                        </WrapInfo>
                    </Grid>
                </Grid>
                <Grid item xs={6}>
                    {seer?.length > 0 && (
                        <WrapInfo>
                            <StyledTextInfoFlex>{translate(intl, KEYS.UI_COMFORT)}</StyledTextInfoFlex>
                            <ButtonGroup fullWidth>
                                {seer.map((calctype, index) => (
                                    <Button key={index} onClick={() => handleClickButton(calctype)}>
                                        {translate(intl, 'UI_' + calctype.toUpperCase())}
                                    </Button>
                                ))}
                            </ButtonGroup>
                        </WrapInfo>
                    )}
                    {dseer?.length > 0 && (
                        <WrapInfo>
                            <StyledTextInfoFlex>{translate(intl, KEYS.UI_DESERTCOMFORT)}</StyledTextInfoFlex>
                            <ButtonGroup fullWidth>
                                {dseer.map((calctype, index) => (
                                    <Button key={index} onClick={() => handleClickButton(calctype)}>
                                        {translate(intl, 'UI_' + calctype.toUpperCase())}
                                    </Button>
                                ))}
                            </ButtonGroup>
                        </WrapInfo>
                    )}
                    {sepr?.length > 0 && (
                        <WrapInfo>
                            <StyledTextInfoFlex>{translate(intl, KEYS.UI_PROCESS)}</StyledTextInfoFlex>
                            <ButtonGroup fullWidth>
                                {sepr.map((calctype, index) => (
                                    <Button key={index} onClick={() => handleClickButton(calctype)}>
                                        {translate(intl, 'UI_' + calctype.toUpperCase())}
                                    </Button>
                                ))}
                            </ButtonGroup>
                        </WrapInfo>
                    )}
                    {(scop.LT.values.length > 0 || scop.MT.values.length > 0 || scop.HT.values.length > 0) && (
                        <WrapInfo>
                            <StyledTextInfoFlex>{translate(intl, KEYS.UI_SPACEHEATER)}</StyledTextInfoFlex>
                            {Object.values(scop).map((v: any) => {
                                return (
                                    v.values.length > 0 && (
                                        <React.Fragment key={v.name}>
                                            <StyledTextInfoFlex>{translate(intl, v.name)}</StyledTextInfoFlex>
                                            <ButtonGroup fullWidth>
                                                {v.values.map((calctype: string, index: number) => (
                                                    <Button key={index} onClick={() => handleClickButton(calctype)}>
                                                        {translate(intl, 'UI_' + calctype.toUpperCase())}
                                                    </Button>
                                                ))}
                                            </ButtonGroup>
                                        </React.Fragment>
                                    )
                                )
                            })}
                        </WrapInfo>
                    )}
                </Grid>
            </Grid>

            {loader && (
                <Box>
                    <StyledLinearProgress />
                    <StyledBoxCenter>
                        <TextInfo>{translate(intl, KEYS.UI_TDSGENERATION_PROGRESS)}</TextInfo>
                    </StyledBoxCenter>
                </Box>
            )}
            {error && (
                <Box>
                    <StyledBoxCenter>
                        <TextError>{translate(intl, KEYS.UI_TDSGENERATION_ERROR)}</TextError>
                    </StyledBoxCenter>
                </Box>
            )}
        </StyledBoxFullWidth>
    )
}

export default DetailsHeader
